import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import appConfig from '../config/appConfig';

/**
 * Global maintenance banner. Visible to ALL users when a super admin enables it
 * in System Settings (device preview) or via a published config.json (global).
 * Reads appConfig and re-renders live on config changes.
 *
 * Design matches the member portal's banner (solid amber gradient, white centered
 * text, AlertTriangle icon, "Notice:" prefix).
 *
 * `fixed` — float at the top of the viewport (used on the login / public pages,
 *   whose full-screen backgrounds would otherwise paint over an in-flow banner —
 *   this is exactly how the member portal renders it). Omit for the authenticated
 *   shell, where it sits in normal flow above the top bar.
 */
export default function MaintenanceBanner({ fixed = false }) {
  const [state, setState] = useState(() => ({
    on: appConfig.getMaintenanceEnabled(),
    msg: appConfig.getMaintenanceMessage(),
  }));

  useEffect(() => appConfig.subscribe(() => setState({
    on: appConfig.getMaintenanceEnabled(),
    msg: appConfig.getMaintenanceMessage(),
  })), []);

  const text = state.on ? (state.msg || 'The portal is undergoing scheduled maintenance. Some features may be temporarily unavailable.') : '';
  if (!text) return null;

  return (
    <div role="status" style={{
      ...(fixed
        ? { position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000 }
        : { position: 'relative', zIndex: 20 }),
      background: 'linear-gradient(90deg, #B45309, #92400E)',
      padding: '10px 20px', display: 'flex', alignItems: 'center',
      justifyContent: 'center', gap: 10,
      color: '#fff', fontSize: 13, fontWeight: 600,
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    }}>
      <AlertTriangle size={16} style={{ flexShrink: 0 }} />
      <span>Notice: {text}</span>
    </div>
  );
}
